import React, {useCallback, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import {useDispatch} from "react-redux";
import axios from "../../../../utils/apiClient";
import {formatDateModern, formatDateWithPattern, logDebug} from "../../../../utils/util";
import cloneDeep from "lodash/cloneDeep";
import {
    Alert,
    Badge,
    Button,
    Col,
    Form,
    InputGroup,
    Modal,
    OverlayTrigger,
    ProgressBar,
    Row,
    Spinner,
    Toast,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip
} from "react-bootstrap";
import {EQHDatePicker} from "../../../../components/datePicker";
import EQHErrorToast from "../../../../components/EQHErrorToast";
import {getDefaultData as careOppPkgDefault} from "../../ManageEvidence/CareOpportunitiesAccordion";
import {getDefaultData as validationPkgDefault} from "../../ManageEvidence/ValidationAccordion";
import {useAuth} from "../../../../context/authContext";
import {FileUpload} from "../../../../components/FileUpload";
import {SimpleDDL} from "../../../../components/dropDownLists/simpleDDL";
import {Filter} from "../../../../components/filters/filterGroup";
import {DDLDataMode, DDLMode, GENERIC_DDL_TYPE} from "../../../../components/dropDownLists/genericDDL";
import {useManageVisitContext} from "../context/ManageVisitContext/ManageVisit.context";
import {setReloadPatientList} from "../../../../actions/manageVisitActions";
import ImmunizationComponent from "../ImmunizationComponent";
import {VisitTab} from "../../Visit/index";
import {AppPropertiesUtil} from "../../../../utils/localStorageUtil";
import {getErrorCode, TRACK_EVENTS, trackPendoEvent} from "../../../../utils/pendoTrack";

const componentName = "manageVisit";

const FOLLOW_UP_ABBREVIATIONS = new Set([
  "ADT-IP", "AAWV", "AWV", "FUH7", "FUH30", "W15", "W30",
  "FUA7", "FUA30", "FUM7", "FUM30", "FMC", "WCV",
  "PPC_Postpartum", "AAP"
]);

const PREVENTION_AND_SCREENINGS_SUBTYPE_ORDER = {
  Biometrics: 0,
  Screenings: 1,
  Immunizations: 2,
  Other: 3,
};

const getOrderedVisitActivitySubTypes = (visitActivity) => {
  const visitActivitySubTypeDtos = visitActivity.visitActivitySubTypeDtos || [];

  if (visitActivity.name !== "Prevention and Screenings") {
    return visitActivitySubTypeDtos;
  }

  return [...visitActivitySubTypeDtos].sort((a, b) => {
    const aOrder = PREVENTION_AND_SCREENINGS_SUBTYPE_ORDER[a?.name] ?? Number.MAX_SAFE_INTEGER;
    const bOrder = PREVENTION_AND_SCREENINGS_SUBTYPE_ORDER[b?.name] ?? Number.MAX_SAFE_INTEGER;

    return aOrder - bOrder;
  });
};

const DEFAULT_PREVENTIVE_CARE_UPDATE_RESULTS_DTO = {
  bloodPressureSystolic: null,
  bloodPressureDiastolic: null,
  a1cWholes: null,
  a1cDecimal: null,
  note: "",
  uploadedFiles: [],
  careGapAdditionalInfos: {},
};

const getDefaultPreventiveCareUpdateResultsDto = () => {
  return cloneDeep(DEFAULT_PREVENTIVE_CARE_UPDATE_RESULTS_DTO);
};

const getToggleStatusValueDisplayName = (toggleStatusValue, dropDownMap) => {
  if (toggleStatusValue === "MARKED_FOR_CLOSURE")
    return "Marked for Closure"
  if (toggleStatusValue === "ORDERED")
    return "Ordered"
  if (toggleStatusValue === "CONFIRMED_DIAGNOSIS")
    return "Confirmed Diagnosis"

  return Object.keys(dropDownMap).find(key => dropDownMap[key] === toggleStatusValue);
};

const getVisitType = (visitActivityName) => {
  if (visitActivityName === "Documentation And Coding") {
    return "DAC";
  }
  if (["Prevention and Screenings", "Needed Referrals", "Medication Review"].includes(visitActivityName)) {
    return "CG";
  }
  return "Other";
};

const toDomIdPart = (value) => String(value ?? "unknown").replace(/[^A-Za-z0-9_-]/g, "-");

const DEFAULT_INFO = {
  requireDOS: false,
  requireStatus: false,
  removeDateLimit: false,
  opportunityDosError: null,
  fuhSelected: false,
  fuhMaxDate: null
};

const getDefaultInfo = () => {
  return cloneDeep(DEFAULT_INFO);
};

const DEFAULT_DATA = {
  selectedGaps: [],
  additionalGapIdsSelected: [],
  errors: [],
  confirmationWarning: "",
  finalizeForBackend: () => false,
  info: getDefaultInfo(),
};


const getDefaultUpdateDTO = () => {
  const dto = cloneDeep(DEFAULT_UPDATE_CARE_GAP_DTO);
  return dto;
};

const DEFAULT_UPDATE_CARE_GAP_DTO = {
  ids: [],
  newStatus: null,
  additionalInfos: [],
  additionalInfoCaption: null,
  exclusionCriteria: [],
};

export const hasSelectedAdditionalDetails = (selectedValues, requiredIds) => {
  if (!requiredIds.length) {
    return true;
  }

  const requiredIdSet = new Set(requiredIds.map((id) => String(id)));
  const selectedCount = Object.keys(selectedValues || {}).filter((gapId) => {
    return (
      requiredIdSet.has(String(gapId)) &&
      selectedValues[gapId]?.length > 0
    );
  }).length;

  return selectedCount === requiredIdSet.size;
};

export const hasCompletedRequiredUpload = (isAttachmentRequired, uploadedFiles, isUploadingFile) => {
  return !isAttachmentRequired || (!isUploadingFile && Boolean(uploadedFiles?.length));
};

export const ManageVisit = ({
  patient,
  setPatientDetailsShow,
  setShowFullHistory,
  setSelectedGaps,
  featureFlag
}) => {

  let btnRef = useRef();

  const isEHRDocumentUploadRequired = AppPropertiesUtil.get("ehr.document.upload.required");
  const careUCDMFeatureFlag = AppPropertiesUtil.get("ce.gaps.UCDM.MFC.LabValue.enabled");
  const isEmpty = require("is-empty");
  const manageVisitContext = useManageVisitContext();
  const dispatch = useDispatch();
  const [showDocAndSetting, setShowDocAndSetting] = useState(false);
  const [showGaps, setShowGaps] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const handleClose = () => {
    setShowGaps(false);
    setShowDocAndSetting(false);
  }
  const [immunizationSelected, setImmunizationSelected] = useState(false);
  const [visitCareGap, setVisitCareGap] = useState({});
  const baseURLMV = process.env.REACT_APP_REST_API_BASE_URL + "/v1/manage-visit";
  const [visitDate, setVisitDate] = useState(null);
  const [suspectId, setSuspectId] = useState(null);
  const [showInactiveActivity, setShowInactiveActivity] = useState(false);

  const [showConfirmDelete, setConfirmDeleteShow] = useState(false);
  const [isDeletingSuspect, setIsDeletingSuspect] = useState(false);
  const handleConfirmDeleteClose = () => {
    if (isDeletingSuspect) return;
    setConfirmDeleteShow(false);
    setSuspectId(null);
  };
  const handleConfirmDeleteShow = (selectedSuspectId) => {
    if (isDeletingSuspect) return;
    setSuspectId(selectedSuspectId);
    setConfirmDeleteShow(true);
  }
  const contracted = manageVisitContext?.patient_get?.contracted;
  const patientId = patient?.id;
  const patientIsPracticeLevelEHR = patient?.isPracticeLevelEHR;

  const [confirmationWarning, setConfirmationWarning] = useState([]);
  const [requiredValues, setRequiredValues] = useState({
    requireUpload: false,
    requireA1C: false,
    displayBloodPressure: false,
    requireBloodPressure: false,
    attestationDDLOptions: [],
    requiredAttestation: false
  })


  const getImmuList = (careGapId) => {
    const immunDoses = listImmuDTO[listImmuDTO.findIndex(x => x.careGapId === careGapId)]?.careGapImmunizationList;
    const immunDosesNew = immunDoses?.filter(doses => doses?.doses > doses?.dosesActual) || [];
    const finalObject = immunDosesNew.filter(doses => {
      if (doses?.immunizationAbbreviation === "RV1" || doses?.immunizationAbbreviation === "RV5") {
        const comparingVal = doses?.immunizationAbbreviation === 'RV1' ? 'RV5' : 'RV1'
        const rv5OrRv1 = immunDoses.find(item => item.immunizationAbbreviation === comparingVal);
        if (rv5OrRv1) {
          const dosesDosesActual = doses?.careGapImmunizationDoseList.filter(e => e.immunizationStatusENUM &&
            (e.immunizationStatusENUM === 'MARKED_FOR_CLOSURE' || e.immunizationStatusENUM === 'CLOSED') && e.dateOfService).length
          const rv5OrRv1DosesActualList = rv5OrRv1?.careGapImmunizationDoseList.filter(e => e.immunizationStatusENUM &&
            (e.immunizationStatusENUM === 'MARKED_FOR_CLOSURE' || e.immunizationStatusENUM === 'CLOSED') && e.dateOfService)
          const rv5OrRv1DosesActual = rv5OrRv1DosesActualList.length
          let whenToDisplay;
          if (doses?.immunizationAbbreviation === "RV1") {
            whenToDisplay = (rv5OrRv1DosesActual < 3) && ((dosesDosesActual + rv5OrRv1DosesActual) <= 3)
          }
          if (doses?.immunizationAbbreviation === "RV5") {
            whenToDisplay = (rv5OrRv1DosesActual < 2) && ((dosesDosesActual + rv5OrRv1DosesActual) <= 3)
          }

          doses.rv5OrRv1ValCompleted = rv5OrRv1DosesActual
          doses.allDateOfServices = rv5OrRv1DosesActualList.map(f => f.dateOfService)
          return whenToDisplay
        }
      }
      return true;
    });
    return finalObject
  }
  const checkDoses = (subVisitActivity) => {
    const immunDoses = subVisitActivity.immunizationsDTO.careGapImmunizationList.filter(doses => doses?.dosesActual >= doses?.doses);
    if (subVisitActivity?.abbreviation === "CIS") {
      if (immunDoses?.length) {
        const immunDoses = subVisitActivity.immunizationsDTO.careGapImmunizationList.filter(doses => (doses.immunizationAbbreviation === "RV5" || doses.immunizationAbbreviation === "RV1") || doses?.dosesActual >= doses?.doses);
        const newArray = immunDoses.filter(doses => {
          if (doses.immunizationAbbreviation === "RV1" || doses.immunizationAbbreviation === "RV5") {
            const comparingVal = doses?.immunizationAbbreviation === 'RV1' ? 'RV5' : 'RV1'
            const rv5OrRv1 = immunDoses.find(item => item.immunizationAbbreviation === comparingVal);
            if (rv5OrRv1) {
              const dosesDosesActual = doses?.careGapImmunizationDoseList.filter(e => e.immunizationStatusENUM &&
                (e.immunizationStatusENUM === 'MARKED_FOR_CLOSURE' || e.immunizationStatusENUM === 'CLOSED') && e.dateOfService).length
              const rv5OrRv1DosesActual = rv5OrRv1?.careGapImmunizationDoseList.filter(e => e.immunizationStatusENUM &&
                (e.immunizationStatusENUM === 'MARKED_FOR_CLOSURE' || e.immunizationStatusENUM === 'CLOSED') && e.dateOfService).length

              const rv5OrRv1Val = dosesDosesActual + rv5OrRv1DosesActual >= 3 || dosesDosesActual >= doses?.doses || rv5OrRv1?.dosesActual >= rv5OrRv1?.doses;
              return rv5OrRv1Val
            }
          }
          return true;
        });
        return newArray;
      }
    }
    return immunDoses;
  }
  const handleImmunizationsSave = (immuObject, id, action, subVisitActivity) => {
    const clonelistImmuDTO = cloneDeep(listImmuDTO)
    const comparingCareGapId = subVisitActivity.immunizationsDTO.careGapId
    const itemFromArrayToUpdateIndex = clonelistImmuDTO.findIndex(n => n.careGapId === comparingCareGapId)
    let immuItemToUpdate = clonelistImmuDTO[itemFromArrayToUpdateIndex]
    let newListImu = []
    let localImuObject = {}
    if (action === 'save') {
      let objectWithFlag = immuObject
      objectWithFlag.selected = true
      localImuObject = objectWithFlag
    }
    else {
      let refreshedBackImuObject = subVisitActivity?.immunizationsDTO?.careGapImmunizationList.find(t => t.id === immuObject.id)
      refreshedBackImuObject.selected = false
      localImuObject = refreshedBackImuObject
    }
    newListImu = { ...immuItemToUpdate, careGapImmunizationList: immuItemToUpdate.careGapImmunizationList.map(immuObject => immuObject.id === localImuObject.id ? localImuObject : immuObject) }
    clonelistImmuDTO[itemFromArrayToUpdateIndex] = newListImu
    setListImmuDTO(clonelistImmuDTO)
    const immunizationSelected = clonelistImmuDTO
      .flatMap(n => n.careGapImmunizationList)
      .map(h => h.selected)
      .some(Boolean)
    if (immunizationSelected) {
      setImmunizationsDTO(newListImu)
    }
    else {
      setImmunizationsDTO({})
    }
    setImmunizationSelected(immunizationSelected)
    handleActionChange({ target: { checked: immunizationSelected } }, subVisitActivity)
  }

  // property to hold the list of selected carevisit Ids
  const [careVisitIds, setCareVisitIds] = useState([]);
  // property to hold the list of selected visit tiles
  const [selectedVisitTiles, setSelectedVisitTiles] = useState([]);
  // property to hold the indexes of the selected tiles, starting from 0
  const [selectedVisitTilesIdx, setSelectedVisitTilesIdx] = useState([]);

  const [fileUploadResetCounter, setFileUploadResetCounter] = useState(0);

  const auth = useAuth();
  const authorizedForViewNewManageVisit = auth.hasAnyAuthority(["AUTH_PD_NEW_MANAGE_VISIT_READ_ACCESS", "AUTH_PD_NEW_MANAGE_VISIT_WRITE_ACCESS"]);


  const [careOpportunityPkg] = useState(() =>
    careOppPkgDefault(visitDate)
  );
  const [preventiveCareUpdateResultsDto, setPreventiveCareUpdateResultsDto] =
    useState(() => getDefaultPreventiveCareUpdateResultsDto());
  const [selectedSuspects, setSelectedSuspects] = useState({
    ids: [],
    conditions: [],
  });
  const [tocPkg] = useState(() => careOppPkgDefault(visitDate));
  const [validationPkg] = useState(() =>
    validationPkgDefault(visitDate)
  );
  const [errors, setErrors] = useState([]);
  const getDefaultData = () => {
    const dto = cloneDeep(DEFAULT_DATA);
    dto.updateCareGapDto = getDefaultUpdateDTO();
    return dto;
  };

  const [isUpdating, setIsUpdating] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const isSearching = false;
  const [isVisitDateRequired, setIsVisitDateRequired] = useState(false);

  const [currentGapSelected, setCurrentGapSelected] = useState({});
  const [currentSuspectSelected, setCurrentSuspectSelected] = useState({});

  const [gapSelectedStatus, setGapSelectedStatus] = useState({});
  const [conditionSelectedStatus, setConditionSelectedStatus] = useState({});

  const [manageVisitData, setManageVisitData] = useState({});

  const [selectedSubActivityGroup, setSelectedSubActivityGroup] = useState({});
  const [actionChangeCount, setActionChangeCount] = useState({});
  const [selectedSubActivityId, setSelectedSubActivityId] = useState([]);

  const [bloodPressureError, setBloodPressureError] = useState(false);
  const [ac1ValueError, setAc1ValueError] = useState(false);

  const [filterValues, setFilterValues] = useState({
    paramValue: [],
    displayValue: [],
  });
  const resetCurrentErrors = () => {
    setErrors([]);
  };

  const updatePatientEvidence = (careOpportunityPkgArg, selectedSuspectsArg, gapSelectedStatus, conditionSelectedStatus) => {
    try {
      careOpportunityPkgArg.finalizeForBackend();
      tocPkg.finalizeForBackend();
      validationPkg.finalizeForBackend();
      const dto = {
        ...preventiveCareUpdateResultsDto,
        patientLastUpdated: manageVisitData.patientLastUpdated,
        visitDate: visitDate || null,
        patientId: patient?.id,
        visitIds: careVisitIds,
        updateCareGapActionDto: careOpportunityPkgArg.selectedGaps.length ? careOpportunityPkgArg.updateCareGapDto : null,
        updateTOCCareGapActionDto: tocPkg.selectedGaps.length
          ? tocPkg.updateCareGapDto
          : null,
        updateValidationCareGapActionDto:
          validationPkg.selectedItems.length &&
            validationPkg.validationType === "Opportunity"
            ? validationPkg.updateDto
            : null,
        updateValidationConditionActionDto:
          validationPkg.selectedItems.length &&
            validationPkg.validationType === "Suspect"
            ? validationPkg.updateDto
            : null,
        updateConditionActionDto: selectedSuspectsArg.ids.length
          ? {
            ids: selectedSuspectsArg.ids,
            visitDate: visitDate || null,
            validation: null,
            icd10DiseaseDto: null,
            forPreviousYear: false,
          }
          : null,
        immunizationsDTO: immunizationsDTO || null
      };
      if (dto.updateCareGapActionDto && gapSelectedStatus.currentStatus) {
        const newStatus = {
          "source": manageVisitData.caregapStatusesDropDownMap[gapSelectedStatus.currentStatus.key],
        }
        dto.updateCareGapActionDto.newStatus = newStatus;
      }
      if (dto.updateConditionActionDto && conditionSelectedStatus.currentStatus) {
        const newStatus = {
          "source": manageVisitData.conditionStatusesDropDownMap[conditionSelectedStatus.currentStatus.key],
        }
        dto.updateConditionActionDto.newStatus = newStatus;
      }
      if (dto.updateCareGapActionDto && gapSelectedStatus.dateOfService) {
        dto.updateCareGapActionDto.newDateOfService = gapSelectedStatus.dateOfService;
      }
      if (dto.updateConditionActionDto && conditionSelectedStatus.dateOfService) {
        dto.updateConditionActionDto.newDateOfService = conditionSelectedStatus.dateOfService;
      }
      logDebug(() => dto);

      axios
        .post(baseURLMV + `/update`, dto, {
          headers: { "Content-Type": "application/json" },
        })
        .then(async (response) => {
          trackPendoEvent(TRACK_EVENTS.MANAGE_VISIT_UPDATE_SUBMIT, {
            source_module: "ManageVisit.updatePatientEvidence",
            visit_count: careVisitIds.length,
            suspect_count: selectedSuspectsArg?.ids?.length || 0,
            has_attachments: preventiveCareUpdateResultsDto.uploadedFiles.length > 0,
            success: true
          });

          if (response.data.visitDate) {
            setVisitDate(response.data.visitDate);
          }

          if (careOpportunityPkg.selectedGaps.length) {
            visitCareGap["status"] = careOpportunityPkg.updateCareGapDto.newStatus;
            setVisitCareGap({});
          }
          setImmunizationSelected(false)
          setImmunizationsDTO({});
          handleClose();
          resetState();
          setActionChangeCount({});
          setCareVisitIds([]);
          setSelectedVisitTiles([]);
          setSelectedVisitTilesIdx([]);
          setVisitDate(null);
          setSelectedSubActivityGroup({});
          setManageVisitData({});
          setRequiredValues({
            requireUpload: false,
            requireA1C: false,
            displayBloodPressure: false,
            requireBloodPressure: false,
            attestationDDLOptions: [],
            requiredAttestation: false
          })
          resetAfterUpdate();
          setPreventiveCareUpdateResultsDto(() => getDefaultPreventiveCareUpdateResultsDto());
          dispatch(setReloadPatientList(true));
          refreshManageVisit();
          setIsButtonDisabled(false);
          setFileUploadResetCounter(fileUploadResetCounter + 1);
        })
        .catch((error) => {
          trackPendoEvent(TRACK_EVENTS.MANAGE_VISIT_UPDATE_SUBMIT, {
            source_module: "ManageVisit.updatePatientEvidence",
            visit_count: careVisitIds.length,
            suspect_count: selectedSuspectsArg?.ids?.length || 0,
            has_attachments: preventiveCareUpdateResultsDto.uploadedFiles.length > 0,
            success: false,
            error_code: getErrorCode(error, "manage_visit_update_submit_failed")
          });
          logDebug(() => error);
          formatErrorMessage(error);
          checkAndUpdateCareVisitAdditionInfoRequiredValues(selectedVisitTiles);
          setPreventiveCareUpdateResultsDto((old) => ({
            ...getDefaultPreventiveCareUpdateResultsDto(),
            note: old.note,
            bloodPressureSystolic: old.bloodPressureSystolic,
            bloodPressureDiastolic: old.bloodPressureDiastolic,
            a1cWholes: old.a1cWholes,
            a1cDecimal: old.a1cDecimal,
            uploadedFiles: old.uploadedFiles,
            careGapAdditionalInfos: old.careGapAdditionalInfos
          }));
          refreshManageVisit();
          setIsButtonDisabled(false);
        });
    } catch (e) {
      logDebug(() => e);
      resetState();
      setIsButtonDisabled(false);
    }
  };

  const formatErrorMessage = (error) => {
    if (error?.response?.data?.message?.includes("|")) {
      let errors = error?.response?.data?.message.split("|");
      setErrors(errors);
    } else {
      setErrors([error?.response?.data?.message]);
    }
  };

  const resetState = () => {
    setSelectedSuspects({
      ids: [],
      conditions: [],
    });

    setImmunizationsDTO({});
    setGapSelectedStatus({});
    setConditionSelectedStatus({});
    setCurrentGapSelected({})
    setCurrentSuspectSelected({})
    refreshManageVisit();
    setCareVisitIds([]);

  };

  const [errorFromDateComponent, setErrorFromDateComponent] = useState("")
  const handleVisitChange = (value) => {
    let strDate = value.strDate;
    if (value?.error) {
      setErrorFromDateComponent(value.error)
      strDate = '';
    }
    else {
      setErrorFromDateComponent('')
    }
    setVisitDate(strDate);
    setGapSelectedStatus({ ...gapSelectedStatus, dateOfService: strDate });
    setConditionSelectedStatus({ ...conditionSelectedStatus, dateOfService: strDate });
    setCurrentGapSelected({ ...currentGapSelected, dateOfService: strDate });
    setCurrentSuspectSelected({ ...currentSuspectSelected, dateOfService: strDate });

  };

  const checkVisitDateRequired = () => {
    setIsVisitDateRequired(document.getElementsByClassName('visitTile active').length > 0)
  }

  const handleEdit = (groupName, selected) => {
    let newCurrentSelected;
    let selectedMap;
    let newCurrentStatus = { key: "" };
    if (groupName === "CARE_GAP") {
      newCurrentSelected = { ...gapSelectedStatus };
      selectedMap = selectedGapMap;
      if (gapSelectedStatus.currentStatus) {
        newCurrentStatus = { ...gapSelectedStatus.currentStatus };
      }

    } else if (groupName === "SUSPECT") {
      newCurrentSelected = { ...conditionSelectedStatus };
      selectedMap = selectedConditionMap;
      if (conditionSelectedStatus.currentStatus) {
        newCurrentStatus = { ...conditionSelectedStatus.currentStatus };
      }
    } else {
      alert("Action type not supported");
      return;
    }

    //use default value if there is only 1 gap selected or 1+ condition selected and there is no previous status selection
    let keys = Object.keys(selectedMap);
    if (keys.length === 1 && groupName === "CARE_GAP" && !gapSelectedStatus.currentStatus) {
      newCurrentStatus.key = selectedMap[keys[0]];
    } else if (groupName === "SUSPECT" && !conditionSelectedStatus.currentStatus) {
      newCurrentStatus.key = selectedMap[keys[0]];
    } else if (groupName === "SUSPECT" && conditionSelectedStatus.currentStatus) {
      newCurrentStatus.key = conditionSelectedStatus.currentStatus;
    } //else leave as -Selected-

    newCurrentSelected.groupName = groupName;
    newCurrentSelected.selected = selected;
    newCurrentSelected.currentStatus = newCurrentStatus;
    newCurrentSelected.dateOfService = visitDate



    if (groupName === "CARE_GAP") {
      setCurrentGapSelected(newCurrentSelected);
      setShowGaps(true)
    } else if (groupName === "SUSPECT") {
      setCurrentSuspectSelected(newCurrentSelected);
      setShowDocAndSetting(true)
    }

  };

  const loadAlreadyProcessed = (mvData) => {
    //Used to show confirmation warninng message
    const processedIds = mvData.manageVisitActivityTypeDtos
      ?.flatMap((visitActivity) => visitActivity.visitActivitySubTypeDtos || [])
      .flatMap((visitActivitySubType) => visitActivitySubType.visitActivities || [])
      .filter((subVisitActivity) => subVisitActivity.toggleStatus)
      .map((subVisitActivity) => subVisitActivity.id) || [];

    if (processedIds.length) {
      setSelectedSubActivityId((currentIds) => [...currentIds, ...processedIds]);
    }
  };

  const [selectedGapMap, setSelectedGapMap] = useState({});
  const [selectedConditionMap, setSelectedConditionMap] = useState({});
  const [immunizationsDTO, setImmunizationsDTO] = useState({});
  const handleActionChange = (event, subVisitActivity) => {
    let dropDownMap = {};
    let setterMap;
    let valueMap;
    if (subVisitActivity.actionType === "CARE_GAP") {
      dropDownMap = manageVisitData.caregapStatusesDropDownMap;
      setterMap = setSelectedGapMap;
      valueMap = selectedGapMap;
    } else if (subVisitActivity.actionType === "SUSPECT") {
      dropDownMap = manageVisitData.conditionStatusesDropDownMap;
      setterMap = setSelectedConditionMap;
      valueMap = selectedConditionMap;
    }
    let dropDownKey = getToggleStatusValueDisplayName(subVisitActivity.toggleStatusValue, dropDownMap);
    if (event.target.checked) {
      let object = { ...valueMap };
      object[subVisitActivity.id] = dropDownKey
      if (setterMap) {
        setterMap(object);
      }
    } else {
      delete valueMap[subVisitActivity.id];
    }
    if (event.target.checked) {
      selectedSubActivityId.push(subVisitActivity.id);
      setSelectedSubActivityId([...selectedSubActivityId]);
    } else {
      const indexOfActivity = selectedSubActivityId.indexOf(subVisitActivity.id);
      if (indexOfActivity > -1) {
        selectedSubActivityId.splice(indexOfActivity, 1);
        setSelectedSubActivityId([...selectedSubActivityId]);
      }
    }

    const currentActionCount = actionChangeCount[subVisitActivity.actionType] || 0;

    const currentGap = currentGapSelected?.currentStatus?.value;
    let nextSelectedSubActivityGroup = selectedSubActivityGroup;

    if (event.target.checked && !subVisitActivity.toggleStatus) {
      const cloneSelectedSubActivityGroup = {
        ...(selectedSubActivityGroup[subVisitActivity.actionType] || {})
      };
      if (!cloneSelectedSubActivityGroup[currentGap]) {
        cloneSelectedSubActivityGroup[currentGap] = [];
      }
      cloneSelectedSubActivityGroup[currentGap] = [
        ...cloneSelectedSubActivityGroup[currentGap],
        subVisitActivity
      ];
      nextSelectedSubActivityGroup = {
        ...selectedSubActivityGroup,
        [subVisitActivity.actionType]: cloneSelectedSubActivityGroup
      };
      setSelectedSubActivityGroup(nextSelectedSubActivityGroup);
      //Increment action change counter
      setActionChangeCount({ ...actionChangeCount, [subVisitActivity.actionType]: currentActionCount + 1 });
    } else if (!event.target.checked && !subVisitActivity.toggleStatus) {
      const newcloneSelectedSubActivityGroup = {
        ...(selectedSubActivityGroup[subVisitActivity.actionType] || {})
      };
      if (newcloneSelectedSubActivityGroup[currentGap]) {
        const arr = newcloneSelectedSubActivityGroup[currentGap]
          .filter(e => e.id !== subVisitActivity.id);
        if (arr.length < 1) {
          delete newcloneSelectedSubActivityGroup[currentGap];
        } else {
          newcloneSelectedSubActivityGroup[currentGap] = arr;
        }

        nextSelectedSubActivityGroup = {
          ...selectedSubActivityGroup,
          [subVisitActivity.actionType]: newcloneSelectedSubActivityGroup
        };
        setSelectedSubActivityGroup(nextSelectedSubActivityGroup);
        setActionChangeCount({ ...actionChangeCount, [subVisitActivity.actionType]: currentActionCount - 1 });
      }
    }

    checkForRequiredField(event, subVisitActivity, null, nextSelectedSubActivityGroup);
  };

  const checkForRequiredField = (event, subVisitActivity, selectedStatus, selectedSubActivityGroupToCheck = selectedSubActivityGroup) => {
    let statusToCheck = {}
    if (selectedStatus?.currentStatus?.key) {
      statusToCheck = selectedStatus
    }
    else if (currentGapSelected?.currentStatus?.key) {
      statusToCheck = currentGapSelected
    }

    let allRequiredA1C = []
    let hasUCDM = []
    let allRequiredUpload = []
    let allDisplayBloodPressure = []
    let allRequireBloodPressure = []
    let attestationDDLOptions = []

    //map visits
    requiredValues.attestationDDLOptions.forEach((option) => {
      if (option.visitGap) {
        attestationDDLOptions.push(option);
      }
    });
    Object.keys(selectedSubActivityGroupToCheck).forEach((subActInd) => {
      if (selectedSubActivityGroupToCheck[subActInd]) {
        const activityGroup = selectedSubActivityGroupToCheck[subActInd];
        Object.keys(activityGroup).forEach((statusIndex) => {
          if (activityGroup[statusIndex]) {
            const activityGroupForStatus = activityGroup[statusIndex];
            Object.keys(activityGroupForStatus).forEach((activityIdx) => {
              const activity = activityGroupForStatus[activityIdx];
              if (((isEmpty(statusToCheck) || (!isEmpty(statusToCheck) && isEmpty(statusToCheck.currentStatus))) && activity.toggleStatusValue === 'MARKED_FOR_CLOSURE')
                || (!isEmpty(statusToCheck) && statusToCheck.currentStatus && (statusToCheck.currentStatus.key.includes('Marked') || statusToCheck.currentStatus.key.includes('Previously')))) {
                hasUCDM.push(activity?.abbreviation)
                allRequiredA1C.push(activity?.requireA1C)
                allDisplayBloodPressure.push(activity?.displayBloodPressure)
                allRequireBloodPressure.push(activity?.requireBloodPressure)
              }
              let attDDLVal = {};
              if (activity?.additionalInfoCaption && activity.additionalInfoOptions?.length) {
                attDDLVal = {
                  gapId: activity.id,
                  visitGap: false,
                  abbreviation: activity.abbreviation,
                  caption:
                    activity.abbreviation +
                    " " +
                    activity.additionalInfoCaption,
                  options: activity.additionalInfoOptions,
                }
              }
              if (attDDLVal?.options?.length
                && (activity?.toggleStatusValue === 'MARKED_FOR_CLOSURE' ||
                  (!isEmpty(statusToCheck) && statusToCheck.currentStatus && (statusToCheck.currentStatus.key.includes('Marked') || statusToCheck.currentStatus.key.includes('Previously'))))) {
                attestationDDLOptions.push(attDDLVal);
              } else if (attestationDDLOptions.length) {
                const indexOfAttDDL = attestationDDLOptions.findIndex((e) => e.gapId === attDDLVal.gapId);
                if (indexOfAttDDL > -1) {
                  attestationDDLOptions.splice(indexOfAttDDL, 1);
                };
              }
              allRequiredUpload.push(activity?.requireFileUpload)
            });
          }
        });
      }
    });
    const requiredAttestation = attestationDDLOptions.length > 0;
    setRequiredValues({
      ...requiredValues, requireUpload: allRequiredUpload.some(Boolean),
      hasUCDM: hasUCDM.some(e => e === 'UCDM'),
      requireA1C: allRequiredA1C.some(Boolean),
      displayBloodPressure: allDisplayBloodPressure.some(Boolean),
      requireBloodPressure: allRequireBloodPressure.some(Boolean),
      attestationDDLOptions: attestationDDLOptions,
      requiredAttestation: requiredAttestation
    })
  }

  const updateSelected = () => {
    setIsUpdating(true);
    let { conditionSelectedStatus } = applyStatus(currentSuspectSelected)
    let { gapSelectedStatus } = applyStatus(currentGapSelected)

    try {
      //update care visit tile
      const cloneData = getDefaultData();
      cloneData.selectedGaps = [];
      selectedSuspects.conditions = [];

      if (selectedSubActivityGroup["CARE_GAP"]) {
        cloneData.updateCareGapDto.ids = [];
        const activityGroup = selectedSubActivityGroup["CARE_GAP"];
        Object.keys(activityGroup).forEach((statusIndex) => {
          if (activityGroup[statusIndex]) {
            const activityGroupForStatus = activityGroup[statusIndex];
            Object.keys(activityGroupForStatus).forEach((activityIdx) => {
              const activity = activityGroupForStatus[activityIdx];
              cloneData.selectedGaps.push(activity);
              cloneData.updateCareGapDto.ids.push(activity.id);
            });

          }
        });
      }

      if (selectedSubActivityGroup["SUSPECT"]) {
        selectedSuspects.ids = [];
        const activityGroup = selectedSubActivityGroup["SUSPECT"];
        Object.keys(activityGroup).forEach((statusIndex) => {
          if (activityGroup[statusIndex]) {
            const activityGroupForStatus = activityGroup[statusIndex];
            Object.keys(activityGroupForStatus).forEach((activityIdx) => {
              const activity = activityGroupForStatus[activityIdx];
              selectedSuspects.conditions.push(activity);
              selectedSuspects.ids.push(activity.id);
            });
          }
        });
      }
      updatePatientEvidence(cloneData, selectedSuspects, gapSelectedStatus, conditionSelectedStatus);
    } catch (e) {
      logDebug(() => e);
      setIsUpdating(false);
    }
    setIsUpdating(false);
    resetAfterUpdate();
  };



  const resetAfterUpdate = () => {
    setSelectedGapMap({});
    setSelectedConditionMap({});
    checkVisitDateRequired();
  };

  const handleOptionChange = (event, currentSelected) => {
    if (event.target.value && currentSelected?.selected[event.target.value]) {
      let currentStatus = { "name": event.target.name, "value": currentSelected.selected[event.target.value], "key": event.target.value };
      let newCurrentSelected = { ...currentSelected };
      newCurrentSelected.currentStatus = currentStatus;

      if (currentSelected?.groupName === "CARE_GAP") {
        setCurrentGapSelected(newCurrentSelected);
        const crrKey = Object.keys(selectedSubActivityGroup?.CARE_GAP)
        if (crrKey.length > 0) {
          const currentValue = selectedSubActivityGroup?.CARE_GAP?.[crrKey[0]]
          setSelectedSubActivityGroup({ ...selectedSubActivityGroup, CARE_GAP: { [currentStatus.value]: currentValue } })
        }
      } else if (currentSelected?.groupName === "SUSPECT") {
        setCurrentSuspectSelected(newCurrentSelected);
      }

      checkForRequiredField(null, null, newCurrentSelected);
    }
  };

  const handleCareOpportunityChange = (gapId) => {
    setSelectedGaps(gapId);
  }

  const applyStatus = (currentSelected) => {
    let selectedStatus = {}
    let gapSelectedStatus = {}
    let conditionSelectedStatus = {}
    if (selectedSubActivityGroup[currentSelected["groupName"]]) {
      let activityGroup = selectedSubActivityGroup[currentSelected["groupName"]];

      if (activityGroup) {
        let ids = [];
        Object.keys(activityGroup).forEach((statusName) => {
          ids.push(...activityGroup[statusName]);
        });

        const nextSelectedSubActivityGroup = {
          ...selectedSubActivityGroup,
          [currentSelected["groupName"]]: []
        };
        if (currentSelected.currentStatus) {
          nextSelectedSubActivityGroup[currentSelected["groupName"]][currentSelected["currentStatus"].value || currentSelected["currentStatus"].key.toUpperCase().replaceAll(' ', '_')] = ids;
        }
        setSelectedSubActivityGroup(nextSelectedSubActivityGroup);
      }

      if (currentSelected.groupName === "CARE_GAP") {
        if (currentSelected.currentStatus.key !== "") {
          setGapSelectedStatus(currentSelected);
          selectedStatus = currentSelected
          gapSelectedStatus = currentSelected;
        }
      } else if (currentSelected.groupName === "SUSPECT") {
        setConditionSelectedStatus(currentSelected);
        conditionSelectedStatus = currentSelected;
      } else {
        alert("Action type not supported");
        return;
      }
    }
    checkForRequiredField(null, null, selectedStatus);
    return { selectedSubActivityGroup, gapSelectedStatus, conditionSelectedStatus }
  };

  const [isConnected, setIsConnected] = useState(false);

  const isConnectedToEHR = useCallback(() => {
    return patientIsPracticeLevelEHR;
  }, [patientIsPracticeLevelEHR]);

  const isDisplayMessage = () => {
    return isConnectedToEHR();
  }

  const isDisplayEvidenceRequiredStar = () => {
      return isEHRDocumentUploadRequired ? disableUpdateButtonRequirements().isAttachmentRequired : (!isConnected && disableUpdateButtonRequirements().isAttachmentRequired );
  }

  useEffect(() => {
    const checkConnection = () => {
      const connected = isConnectedToEHR();
      setIsConnected(connected);
    };

    checkConnection();
  }, [isConnectedToEHR]);

  const handleDeleteSuspect = async (id, patient) => {
    if (!id || isDeletingSuspect) return;
    setIsDeletingSuspect(true);
    try {
      await manageVisitContext.findSuspectDelete(id, patient);
      await refreshManageVisit();
      setConfirmDeleteShow(false);
      setSuspectId(null);
    } catch (error) {
      formatErrorMessage(error);
    } finally {
      setIsDeletingSuspect(false);
    }
  }

  useEffect(() => {
  }, [showGaps, showDocAndSetting]);

  const refreshManageVisit = async () => {
    if (patient?.id) {
      await Promise.all([
        manageVisitContext.findPatientGet(patient?.id),
        manageVisitContext.findPatientPost(patient?.id)
      ]);
    }
  };

  const [listImmuDTO, setListImmuDTO] = useState([])
  useEffect(() => {
    const response = manageVisitContext?.patient_get;
    if (response) {
      setManageVisitData(response);
      loadAlreadyProcessed(response);
      const immuDto = response?.manageVisitActivityTypeDtos?.flatMap((visitActivity) => visitActivity.visitActivitySubTypeDtos
        .filter(e => returnActiveOrInActive(e?.visitActivities || []).some(Boolean))
        .flatMap((visitActivitySubType) => returnActiveOrInActive(visitActivitySubType.visitActivities).filter((subVisitActivity) => !isEmpty(subVisitActivity) && !isEmpty(subVisitActivity.immunizationsDTO)))
      )
      if (immuDto?.length) {
        setListImmuDTO(immuDto.flatMap(e => e.immunizationsDTO))
      }
    }
    if (response?.visitDate) {
      setVisitDate(response?.visitDate);
    }
  // The hydration effect should run only when the context payload changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manageVisitContext.patient_get])



  useEffect(() => {
    resetCurrentErrors();
    setVisitDate(null);
    refreshManageVisit();
  // Patient change is the intentional refresh boundary; refreshManageVisit would widen this.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const disableUpdateButtonRequirements = useCallback(() => {

    let isAttachmentRequired = false;
    let additionalValuesIds = [];

    if (selectedSubActivityGroup["CARE_GAP"]) {
      const activityGroup = selectedSubActivityGroup["CARE_GAP"];
      Object.keys(activityGroup).forEach((statusIndex) => {
        if (activityGroup[statusIndex]) {
          const activityGroupForStatus = activityGroup[statusIndex];
          Object.keys(activityGroupForStatus).forEach((activityIdx) => {
            const activity = activityGroupForStatus[activityIdx];

            if (((isEmpty(currentGapSelected) || (!isEmpty(currentGapSelected) && isEmpty(currentGapSelected.currentStatus))) && activity.toggleStatusValue === 'MARKED_FOR_CLOSURE')
              || (!isEmpty(currentGapSelected) && currentGapSelected.currentStatus && (currentGapSelected.currentStatus.key === 'Marked For Closure' || currentGapSelected.currentStatus.key.includes('Marked') || currentGapSelected.currentStatus.key.includes('Previously')))) {
              if (activity.abbreviation !== "EED"
                && activity.additionalInfoOptions?.length) {
                additionalValuesIds.push(activity.id);
              }
            }

            if (activity.requireFileUpload && contracted) {
              isAttachmentRequired = activity.requireFileUpload;
            }
          });
        }
      });
    }

    if (selectedSubActivityGroup["SUSPECT"]) {
      let suspectActivityGroup = selectedSubActivityGroup["SUSPECT"];
      let suspectActivityGroupKeys = Object.keys(suspectActivityGroup);

      suspectActivityGroupKeys.forEach((statusIndex) => {
        if (suspectActivityGroup[statusIndex]) {
          const activityGroupForStatus = suspectActivityGroup[statusIndex];
          Object.keys(activityGroupForStatus).forEach((activityIdx) => {
            const activity = activityGroupForStatus[activityIdx];
            if (activity.requireFileUpload && contracted) {
              isAttachmentRequired = activity.requireFileUpload;
            }
          });
        }
      });
    }

    if (selectedSubActivityGroup["CHRONIC_CONDITION"]) {
      const activityGroup = selectedSubActivityGroup["CHRONIC_CONDITION"];
      Object.keys(activityGroup).forEach((statusIndex) => {
        if (activityGroup[statusIndex]) {
          const activityGroupForStatus = activityGroup[statusIndex];
          Object.keys(activityGroupForStatus).forEach((activityIdx) => {
            const activity = activityGroupForStatus[activityIdx];
            if (activity.requireFileUpload && contracted) {
              isAttachmentRequired = activity.requireFileUpload;
            }
          });
        }
      });
    }

    for (let visit of selectedVisitTiles) {
      if ((visit.abbreviation === "FUH7" || visit.abbreviation === "FUH30" || visit.abbreviation === "FUA7" || visit.abbreviation === "FUA30"
            || visit.abbreviation === "FUM7" || visit.abbreviation === "FUM30" || visit.abbreviation === "FMC") 
            && visit.additionalInfoOptions?.length) {
        additionalValuesIds.push(visit.visitId);
      }
    }

    let isSelectedVisitRequiredUpload = selectedVisitTiles.some(visit => visit.requireFileUpload);

    let attachmentRequired = (isAttachmentRequired || isSelectedVisitRequiredUpload);

    return {
      isAttachmentRequired: attachmentRequired,
      additionalValuesIds: additionalValuesIds
    }
  }, [currentGapSelected, selectedVisitTiles, selectedSubActivityGroup, contracted, isEmpty]);

  const checkCareGapAdditionalInfos = () => {
    const selectedGaps = requiredValues?.attestationDDLOptions || [];
    const selectedValues = preventiveCareUpdateResultsDto?.careGapAdditionalInfos || {};

    const fulfilled = selectedGaps.filter(e => selectedValues?.[e.gapId] === undefined || selectedValues?.[e.gapId] === null || selectedValues?.[e.gapId]?.length === 0)
    return fulfilled.length < 1
  }



  const checkVisitDate = () => {
    return Boolean((selectedVisitTiles?.length > 0 && visitDate) || selectedVisitTiles?.length === 0 || immunizationSelected)
  }

  const disableUpdateButton = () => {
    let { isAttachmentRequired, additionalValuesIds } = disableUpdateButtonRequirements();
    let checkAttachment = hasCompletedRequiredUpload(
      isAttachmentRequired,
      preventiveCareUpdateResultsDto.uploadedFiles,
      isUploadingFile
    );
    let checkAdditionalDetails = checkAdditionalDetailsMethod(additionalValuesIds)
    let checkCareGapAdditional = checkCareGapAdditionalInfos()

    let hasActiveVisitSelected = manageVisitData?.careVisitDtos
      ?.filter(dto => careVisitIds.includes(dto.visitId))
      ?.filter(dto => dto.careGapStatus !== "Marked for Closure" && dto.careGapStatus !== "Closed"
        && dto.careGapStatus !== "Gap Ineligible" && dto.careGapStatus !== "Excluded")
      ?.length > 0;

    const checkDOS = visitDate || currentGapSelected?.dateOfService || currentSuspectSelected?.dateOfService

    return !(
      (hasActiveVisitSelected && checkAttachment && checkAdditionalDetails && checkDOS)
      || (
        (
          (
            (checkAdditionalDetails && checkAttachment && checkDOS)
            && (
              (actionChangeCount["CARE_GAP"] && actionChangeCount["CARE_GAP"] > 0)
              || (actionChangeCount["SUSPECT"] && actionChangeCount["SUSPECT"] > 0)
            )
          )
          || (!checkDOS && immunizationSelected)
        )
        && (checkCareGapAdditional || additionalValuesIds.length === 0)
      )
    );
  };
  //, [dateOfService,selectedSubActivityAbbr, actionChangeCount, preventiveCareUpdateResultsDto,selectedSubActivityGroup]);

  const checkAdditionalDetailsMethod = (additionalValuesIds) => {
    if (requiredValues.requireBloodPressure && (isEmpty(preventiveCareUpdateResultsDto.bloodPressureSystolic) || isEmpty(preventiveCareUpdateResultsDto.bloodPressureDiastolic))) {
      return false;
    }

    return hasSelectedAdditionalDetails(
      preventiveCareUpdateResultsDto.careGapAdditionalInfos,
      additionalValuesIds
    );
  }
  const validateAndUpdate = () => {
    resetCurrentErrors();
    setIsButtonDisabled(true);
    updateSelected();
  };

  // collecting the visitId of all selected visits
  const getSelectedVisitId = (selectedVisits) => {
    if (!selectedVisits) return [];
    return selectedVisits.map(visit => visit.visitId);
  }

  //Multiple Tile Selection
  const handleTilesChange = (careVisit, idx) => {
    let selectedVisits = selectedVisitTiles || [];
    let careVisitIndex = selectedVisits.map(sv => sv?.visitId).indexOf(careVisit?.visitId);
    let selectedIndexes = selectedVisitTilesIdx || [];
    if (careVisitIndex === -1) {
      selectedVisits.push(careVisit);
      selectedIndexes.push(idx);
    } else {
      selectedVisits.splice(careVisitIndex, 1);
      selectedIndexes.splice(careVisitIndex, 1);
      const indexOfAttDDL = requiredValues.attestationDDLOptions.findIndex((e) => e.gapId === careVisit?.visitId);
      if (indexOfAttDDL > -1) {
        requiredValues.attestationDDLOptions.splice(indexOfAttDDL, 1);
      };
    }

    setSelectedVisitTiles(selectedVisits);
    setSelectedVisitTilesIdx(selectedIndexes);
    setCareVisitIds(getSelectedVisitId(selectedVisits));
    checkAndUpdateCareVisitAdditionInfoRequiredValues(selectedVisits);
  };

  useEffect(() => {
    checkVisitDateRequired();
  }, [selectedVisitTilesIdx, manageVisitContext, selectedVisitTiles])

  const handleUploadFileChange = (arr) => {
    const currentUploadCount = preventiveCareUpdateResultsDto.uploadedFiles?.length || 0;
    const nextUploadCount = Array.isArray(arr) ? arr.length : 0;
    if (nextUploadCount > 0 && currentUploadCount === 0) {
      trackPendoEvent(TRACK_EVENTS.MANAGE_VISIT_EVIDENCE_UPLOAD, {
        source_module: "ManageVisit.handleUploadFileChange",
        file_count: nextUploadCount,
        success: true
      });
    }
    setPreventiveCareUpdateResultsDto({
      ...preventiveCareUpdateResultsDto,
      "uploadedFiles": arr,
    });
  };

  // Additional Info
  const checkAndUpdateCareVisitAdditionInfoRequiredValues = (selectedVisits) => {
    if (selectedVisits) {
      // Iterate through all the selected visit and construct the requiredValues
      for (let visit of selectedVisits) {

        if (!visit.additionalInfoOptions || !visit.additionalInfoOptions.length) {
          continue;
        }

        // check if the required visitIf already exists in the requiredValues attestationDDLOptions
        // if already exists no need to add it again
        // if not exists add it to the list
        const ind = requiredValues.attestationDDLOptions.findIndex(e => e.gapId === visit.visitId);
        if (ind === -1) {
          requiredValues.attestationDDLOptions.push({
            gapId: visit.visitId,
            visitGap: true,
            abbreviation: visit.abbreviation,
            caption: visit.abbreviation + " " + visit.additionalInfoCaption,
            options: visit.additionalInfoOptions,
          })
        }
      }
    }

    let hasRequiredValues = requiredValues.attestationDDLOptions.length > 0;
    setRequiredValues({ ...requiredValues, requiredAttestation: hasRequiredValues })
  }

  const handlePreventiveCareUpdateResultsDtoChange = (key, value) => {
    switch (key) {
      case "bloodPressureSystolic":
      case "bloodPressureDiastolic":
      case "systolicCode":
      case "diastolicCode":
        setBloodPressureError(value < 0 || value > 400);
        break;
      case "a1cWholes":
      case "a1cDecimal":
        setAc1ValueError(value > 20.9);
        break;
      default:
        break;
    }
    setPreventiveCareUpdateResultsDto({
      ...preventiveCareUpdateResultsDto,
      [key]: value,
    });
  };

  const handleAttestationChangeForFilter = (value, gapId, abb) => {
    setFilterValues(value);
    let finalVal = [];
    if (value?.paramValue?.length) {
      value.paramValue.forEach((val, index) => {
        finalVal.push({ id: val, description: value.displayValue[index], selected: true });
      })
    }
    setPreventiveCareUpdateResultsDto({
      ...preventiveCareUpdateResultsDto,
      careGapAdditionalInfos: {
        ...preventiveCareUpdateResultsDto.careGapAdditionalInfos,
        [gapId]: finalVal,
      },
    });
  };
  const handleAttestationChange = (value, gapId) => {
    let finalVal = [];
    if (value) {
      finalVal.push({ id: value.id, description: value.description, selected: true });
    }
    setPreventiveCareUpdateResultsDto({
      ...preventiveCareUpdateResultsDto,
      careGapAdditionalInfos: {
        ...preventiveCareUpdateResultsDto.careGapAdditionalInfos,
        [gapId]: finalVal,
      },
    });
  };

  /*
  This method checks if the label should be displayed or not with various scenarios.
  */
  const displaySubLabel = (arrProvided, val) => {
    let bool = false
    let arr = []
    if (val === "visitActivities") {
      arr = arrProvided
    }
    else {
      arr = arrProvided.flatMap(m => m.visitActivities)
    }
    if (showInactiveActivity) {

      bool = arr.some(e => (checkFilter(e.currentStatus) || hasDosesCompleted(e)))
      return bool
    }
    else {
      bool = arr.some(e => (!checkFilter(e.currentStatus)))
      return bool
    }
  }

  const returnActiveOrInActive = (activities) => {
    if (showInactiveActivity) {
      return activities.filter(e => (checkFilter(e.currentStatus) || hasDosesCompleted(e)))
    }
    else {
      return activities.filter(e => (!checkFilter(e.currentStatus)))
    }
  }

  const hasDosesCompleted = (e) => {
    const isCompleted = (e.abbreviation === 'IMA' || e.abbreviation === 'CIS')
      && e.immunizationsDTO.careGapImmunizationList.some(n => n.dosesActual >= n.doses)
    return isCompleted
  }

  const checkFilter = (currentStatus) => {
    const retValue = currentStatus === "MARKED_FOR_CLOSURE" || currentStatus === "GAP_INELIGIBLE" || currentStatus === "EXCLUDED" || currentStatus === "CLOSED" || currentStatus === "CONFIRMED_DIAGNOSIS" || currentStatus === "CONFIRMED_DOES_NOT_HAVE_DIAGNOSIS" || currentStatus === "UNABLE_TO_COMPLETE";
    return retValue
  }

  const checkforActivityExists = (activityType) => {
    return manageVisitData?.manageVisitActivityTypeDtos?.flatMap(t => t.visitActivitySubTypeDtos)?.flatMap(m => m.visitActivities)?.some(n => n.actionType === activityType && !checkFilter(n.currentStatus))
  }

  const getActivityLabelByType = (subActivity) => {
    return (subActivity?.displayNameAbbrev || subActivity?.displayName || "").trim();
  }


  const verifySubActivity = (subActivity) => {
    return subActivity?.currentStatus !== '' && !isEmpty(getActivityLabelByType(subActivity))
  }

  const parseLabel = (subVisitActivity) => {
    const baseLabel = getActivityLabelByType(subVisitActivity);
    const isCareGap = subVisitActivity?.actionType === "CARE_GAP";
    let currentStatusDisplayName = " (" + subVisitActivity.currentStatusDisplayName + ")";
    let lbl = isCareGap ? baseLabel + currentStatusDisplayName : baseLabel;
    Object.keys(selectedSubActivityGroup).forEach((subActInd) => {
      if (selectedSubActivityGroup[subActInd]) {
        const activityGroup = selectedSubActivityGroup[subActInd];
        Object.keys(activityGroup).forEach((statusIndex) => {
          if (activityGroup[statusIndex]) {
            const activityGroupForStatus = activityGroup[statusIndex];
            Object.keys(activityGroupForStatus).forEach((activityIdx) => {
              const activity = activityGroupForStatus[activityIdx];
              if (activity?.id === subVisitActivity?.id && isCareGap) {
                lbl = `${baseLabel} (${subVisitActivity.currentStatusDisplayName} -> ${subVisitActivity.toggleStatusName})`
              }
            });
          }
        });
      }
    })
    return lbl
  }

  const showLabValue = () => {
    return (
      requiredValues.requireA1C ||
      requiredValues.displayBloodPressure ||
      requiredValues.requiredAttestation) &&
      (!currentGapSelected.currentStatus?.key.includes('Refused') &&
      !currentGapSelected.currentStatus?.key.includes('Ordered') &&
      !currentGapSelected.currentStatus?.key.includes('Excluded')
    )
  }

  const checkLabResults = () => {
    if (showLabValue() && (requiredValues?.hasUCDM && careUCDMFeatureFlag)) {
      return (
        !preventiveCareUpdateResultsDto?.a1cWholes ||
        !preventiveCareUpdateResultsDto?.a1cDecimal ||
        ac1ValueError
      )
    } else {
      return false
    }
  }

  return (
    <>
      <Row>
        <Col className="text-end">
          <Button
            variant="secondary"
            id={`${componentName}-patientHistory`}
            onClick={() => dispatch(setShowFullHistory(true))}
            className="border me-3"
          >
            History
          </Button>
        </Col>
      </Row>

      {authorizedForViewNewManageVisit ? (
        <div className="bg-white border rounded-3 mt-4">
          <VisitTab visitsDetails={manageVisitData ?? {}}
            patient={patient}
            visitDate={visitDate}
            refreshManageVisit={refreshManageVisit}
            resetAfterUpdate={resetAfterUpdate}
            handleUploadFileChange={handleUploadFileChange}
            onUploadingChange={setIsUploadingFile}
            fileUploadResetCounter={fileUploadResetCounter}
            visitDocumentationNotRequired={isDisplayMessage()}
            attachmentRequired={disableUpdateButtonRequirements().isAttachmentRequired} />
        </div>
      ) : <>
        <Row>
          <Col>
            <span className="fs-5 fw-bold">Please choose a Visit(s)</span>
          </Col>
        </Row>
        <Row>
          <Col>
            <ToggleButtonGroup
              type="checkbox"
              name="visit-type"
              className="visit-type d-flex flex-wrap mb-3"
              value={selectedVisitTilesIdx}
            >
              {
                //Add tiles dynamically from manage-visit response
                manageVisitContext?.patient_get?.careVisitDtos?.map((careVisit, idx) => {
                  const showEligibilityTooltip =
                    (careVisit.abbreviation === "W15" || careVisit.abbreviation === "W30") &&
                    careVisit.eligibleDate;
                  const isPostpartumVisit = careVisit.abbreviation === "PPC_Postpartum" || careVisit.gapAndYear === "PPC_Postpartum";
                  const visitInfo = (
                    <div>
                      <span id={`${componentName}-visitName-${idx}`} className="visit-name mb-2 fw-bold">
                        {careVisit?.validation === "REJECTED" && careVisit.careGapStatus?.toUpperCase() === "OPEN" && (
                            <i className="text-danger fa-regular fa-circle-exclamation me-1"></i>
                        )}
                        {careVisit.name}
                      </span>
                      {(careVisit.abbreviation === "W15" || careVisit.abbreviation === "W30") && (
                        <Button
                          id={`${componentName}-Activities`}
                          variant="link"
                          onClick={() => { handleCareOpportunityChange(careVisit.visitId); }}
                        >
                        </Button>
                      )}
                      {(careVisit.dueDate && FOLLOW_UP_ABBREVIATIONS.has(careVisit.abbreviation)) && (
                        <span id={`${componentName}-visitDueDate-${idx}`} className="visit-duedate">Follow up by { formatDateWithPattern(careVisit.dueDate, "MM/dd/yyyy") }</span>
                      )}
                      <span id={`${componentName}-visitGapStatus-${idx}`} className="visit-status fst-italic">
                        <span>Status:</span> {careVisit.careGapStatus}
                      </span>
                      {(careVisit.chronicConditionName) && (
                          <span id={`${componentName}-visitDueChronicCondition-${idx}`} className="fst-italic d-block">{careVisit.chronicConditionName}</span>
                      )}
                    </div>
                  );
                  let visitTileContent = visitInfo;
                  if (isPostpartumVisit) {
                    visitTileContent = (
                      <OverlayTrigger
                        placement="auto"
                        overlay={(
                          <Tooltip id={`${componentName}-postpartumTooltip-${idx}`}>
                            Sensitive context. The data indicates that this patient has recently given birth. The details that follow may be incomplete, and outcomes may include loss, adoption, or other outcomes. As you reach out to assist with follow-up care, please proceed with respect, sensitivity, and empathy.
                          </Tooltip>
                        )}
                      >
                        {visitInfo}
                      </OverlayTrigger>
                    );
                  } else if (showEligibilityTooltip) {
                    visitTileContent = (
                      <OverlayTrigger
                        trigger={["hover", "focus"]}
                        placement="top"
                        overlay={<Tooltip>{"Gap Ineligible on " + formatDateModern(careVisit.eligibleDate)}</Tooltip>}
                      >
                        {visitInfo}
                      </OverlayTrigger>
                    );
                  }
                  return (
                    <ToggleButton
                      key={careVisit.visitId ?? careVisit.id ?? idx}
                      id={`${componentName}-visitType-${idx}`}
                      value={idx}
                      onChange={() => { handleTilesChange(careVisit, idx) }}
                      className={`visitTile ${selectedVisitTilesIdx.includes(idx) ? 'active' : ''}`}
                      type="button"
                    >
                      {visitTileContent}
                    </ToggleButton>
                  )
                })
              }
            </ToggleButtonGroup>
          </Col>
        </Row>
        <Row>
          <Col>
            {<div className="bg-white border p-3 rounded-3">
              <Row className="mb-3">
                <Col md={3}>
                  <div className="d-inline-block">
                    <label className="fs-5 fw-bold d-block">Visit Date {isVisitDateRequired && <span className="text-danger">*</span>}</label>
                    <EQHDatePicker
                      id={`${componentName}-patientVisitDatePicker`}
                      className="d-inline-block"
                      value={visitDate}
                      min={null}
                      max="9999-12-31"
                      onChange={(e) => {
                        handleVisitChange(e);
                      }}
                      style={{ maxWidth: "fit-content" }}
                    />
                    {errorFromDateComponent ?
                      <Alert key="danger" variant="danger">
                        {errorFromDateComponent}
                      </Alert> : ""}
                  </div>
                  {isSearching ? (
                    <Spinner
                      className="me-3"
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />

                  ) : ("")}
                </Col>
                <Col>
                  <div className="d-flex justify-content-end align-items-center mb-1 position-relative">
                    {showGaps ? "" :
                      <Badge pill bg="light" text="dark" id={`${componentName}-SuspectsCI`} className="border cursor-p"
                        onClick={() => {
                          setShowInactiveActivity(!showInactiveActivity);
                          setShowGaps(false)
                          setShowDocAndSetting(false)
                        }} >
                        <i className={showInactiveActivity ? "fa fa-minus" : "fa fa-plus"}></i> Show Closed/ Inactive
                      </Badge>
                    }
                    {checkforActivityExists("CARE_GAP") &&
                      <div className="d-flex flex-column align-items-end">
                        {!showInactiveActivity &&
                          <Button
                            disabled={!actionChangeCount["CARE_GAP"] || actionChangeCount["CARE_GAP"] < 1}
                            id={`${componentName}-editActivities`}
                            className="btn btn-primary float-end ms-3"
                            onClick={() => handleEdit("CARE_GAP", manageVisitData.caregapStatusesDropDownMap)}
                          >
                            <i className="fa-solid fa-pen-to-square me-2"></i> Edit
                          </Button>
                        }
                        {showGaps &&
                          <span id={`${componentName}-editMessage`} className="mt-2">
                            Please select all activities you'd like to edit, designate a status, and a date of service
                          </span>
                        }
                      </div>
                    }
                    <div className="position-absolute top-50 end-0 mt-5" style={{ "zIndex": 1 }}>
                      {showGaps ? <EditComponent show={showGaps} handleClose={() => {
                        setShowGaps(false);
                        setCurrentGapSelected({});
                      }} setCurrentSelected={setCurrentGapSelected} currentSelected={currentGapSelected}

                        handleOptionChange={(e) => handleOptionChange(e, currentGapSelected)} /> : ""}
                    </div>
                  </div>
                  {errors?.length > 0 &&
                    <EQHErrorToast
                      className="float-end"
                      errors={errors}
                      isList={true}
                      handleClose={resetCurrentErrors}
                    />
                  }
                  <Modal
                    show={Boolean(confirmationWarning.length)}
                    onHide={(e) => setConfirmationWarning([])}
                    size="md"
                    centered
                  >
                    <Modal.Header closeButton>
                      <Modal.Title>Missed Opportunities</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      <p>One or more visits have required activities that have not been completed. Do you wish to continue?</p>
                      <p><strong>Incomplete Activities</strong></p>
                      {Boolean(confirmationWarning?.length)
                        && confirmationWarning.map((e, warningIndex) => (
                          <div className="d-flex align-items-start" key={`${componentName}-confirmation-${warningIndex}`}>
                            <i className="fa-solid fa-xmark text-danger me-2 mt-1"></i>
                            <div>{e}</div>
                          </div>
                        ))}
                    </Modal.Body>
                    <Modal.Footer>
                      <Button
                        id={`${componentName}-confirmNoBtn`}
                        variant="secondary"
                        onClick={(e) => {
                          setConfirmationWarning([]);
                        }}
                      >
                        No, Go Back
                      </Button>
                      <Button
                        id={`${componentName}-confirmYesBtn`}
                        variant="primary"
                        onClick={(e) => {
                          setConfirmationWarning([]);
                          updateSelected();
                        }}
                      >
                        Yes, Submit
                      </Button>
                    </Modal.Footer>
                  </Modal>

                </Col>

              </Row>

              {//load manage visit type, subtype and its activities
                (manageVisitData.manageVisitActivityTypeDtos) ?
                  manageVisitData.manageVisitActivityTypeDtos.map((visitActivity, idx1) => {
                    return (
                      <Row className="mb-5" key={`${visitActivity.name}-${idx1}`}>
                        {
                          visitActivity?.name === "Documentation And Coding" && (
                            <Col sm={12}>
                              <div className="d-flex flex-column align-items-end">
                                {auth.hasAuthority("AUTH_RISK_ADJUSMENTS_FACTORS_WRITE_ACCESS") &&
                                  <div className="d-block">
                                    <Button
                                      id={`${componentName}-addSuspect`}
                                      className="btn btn-secondary mb-2"
                                      onClick={() => {
                                        manageVisitContext.toggleAddModal(true);
                                        manageVisitContext.setModalType('Suspect')
                                      }}
                                    >
                                      <i className="fa-solid fa-plus-circle me-2"></i> Suspect
                                    </Button>
                                    {contracted ? (
                                      <Button
                                        id={`${componentName}-addConditions`}
                                        className="btn btn-secondary ms-3 mb-2"
                                        onClick={() => {
                                          manageVisitContext.toggleAddModal(true);
                                          manageVisitContext.setModalType('Condition')
                                        }}
                                      >
                                        <i className="fa-solid fa-plus-circle me-2" ></i> Condition
                                      </Button>
                                    ) : null}
                                    {contracted ? (
                                      <Button
                                        id={`${componentName}-addReject`}
                                        className="btn btn-secondary ms-3 mb-2"
                                        onClick={() => {
                                          manageVisitContext.toggleAddModal(true);
                                          manageVisitContext.setModalType('Reject')
                                        }}
                                      >
                                        <i className="fa-solid fa-plus-circle me-2" ></i> Reject
                                      </Button>
                                    ) : null}
                                  </div>
                                }
                                {!showInactiveActivity && checkforActivityExists("SUSPECT") ? <Button id={`${componentName}-editSuspects`} className="btn btn-primary mb-3" disabled={showInactiveActivity || (!actionChangeCount["SUSPECT"] || actionChangeCount["SUSPECT"] < 1)} onClick={() => handleEdit("SUSPECT", manageVisitData.conditionStatusesDropDownMap)}>
                                  <i className="fa-solid fa-pen-to-square me-2" ></i> Edit</Button> : ""}
                                {showDocAndSetting ? <span className="text-end mb-2" id={`${componentName}-editMessage`}>Please select all activities you'd like to edit, designate a status, and a date of service</span> : ""}
                                {showDocAndSetting ? <EditComponent show={showDocAndSetting} handleClose={() => { setShowDocAndSetting(false); setCurrentSuspectSelected({}) }} setCurrentSelected={setCurrentSuspectSelected} currentSelected={currentSuspectSelected} handleOptionChange={(e) => handleOptionChange(e, currentSuspectSelected)} /> : ""}
                              </div>
                            </Col>)
                        }
                        <Col>
                          {(displaySubLabel(visitActivity.visitActivitySubTypeDtos)) &&
                            <div className="d-flex justify-content-between">
                              <label id={`${componentName}-visitActivityTitle-${idx1}`} className="fs-5 fw-bold">{visitActivity.name}</label>
                            </div>
                          }
                          {visitActivity.visitActivitySubTypeDtos?.length
                            ? getOrderedVisitActivitySubTypes(visitActivity)
                              .filter(e => returnActiveOrInActive(e?.visitActivities || []).some(Boolean))
                              .map((visitActivitySubType, idx2) => {
                                return (
                                  <div key={visitActivitySubType.id ?? `${visitActivity.name}-${idx2}`} className="visit-activity-subType">
                                    <span className="d-block visit-action-subtitle">
                                      {displaySubLabel(visitActivitySubType.visitActivities, "visitActivities") && !isEmpty(visitActivitySubType.name) && visitActivitySubType.name !== "None" &&
                                        (
                                          <div className="d-flex justify-content-between">
                                            <span id={`${componentName}-visitActivitySubType-${idx2}`} className="visit-action-subtitle">{visitActivitySubType.name}</span>
                                          </div>
                                        )
                                      }
                                    </span>
                                    <div id={`${componentName}-visitActions-${idx2}`} className="mb-3">
                                      <div className={visitActivity.name === "Treatment Planning" ? "treatment-planning-actions px-3" : ""}>
                                        {visitActivitySubType.visitActivities &&
                                          returnActiveOrInActive(visitActivitySubType.visitActivities)
                                            .map((subVisitActivity, idx3) => {
                                              const visitType = getVisitType(visitActivity.name);
                                              const subVisitActivityInputId = [
                                                componentName,
                                                visitType,
                                                "subVisitActivityIcon",
                                                toDomIdPart(visitActivity.name),
                                                toDomIdPart(visitActivitySubType.name),
                                                toDomIdPart(subVisitActivity.id ?? subVisitActivity.displayName),
                                                idx1,
                                                idx2,
                                                idx3,
                                              ].join("-");
                                              if (!verifySubActivity(subVisitActivity)) {
                                                return null;
                                              }
                                              return (
                                                <div key={subVisitActivity.id ?? `${visitActivitySubType.name}-${idx3}`} className="visit-action">
                                                    <Row>
                                                      <Col>
                                                        {
                                                          subVisitActivity?.toggleStatusValue === "INFO_ONLY" ?
                                                            (<>{subVisitActivity?.currentStatus === "OPEN" ? (<div id={`${componentName}-${visitType}-subVisitActivity-${idx3}`} className="d-flex">
                                                              <i id={subVisitActivityInputId} className="far fa-info-circle mt-1 me-2"></i>{" "}
                                                              <span id={`${componentName}-${visitType}-subVisitActivityLabel-${idx3}`} className="form-check-label">
                                                                {subVisitActivity?.validation === "REJECTED" && subVisitActivity?.currentStatus?.toUpperCase() === "OPEN" && (
                                                                  <i className="text-danger fa-regular fa-circle-exclamation me-1"></i>
                                                                )}
                                                                {getActivityLabelByType(subVisitActivity)}
                                                              </span>
                                                            </div>) :
                                                              (<>{subVisitActivity?.currentStatus === "CLOSED" ? (<div id={`${componentName}-${visitType}-subVisitActivity-${idx3}`} className="d-flex text-success">
                                                                <i id={subVisitActivityInputId} className="far fa-check-circle mt-1 me-2"></i>{" "}
                                                                <span id={`${componentName}-${visitType}-subVisitActivityLabel-${idx3}`} className="form-check-label">
                                                                  {getActivityLabelByType(subVisitActivity)}
                                                                </span>
                                                              </div>) : (null)}</>)}
                                                              {" "}</>
                                                            ) : (<div id={`${componentName}-${visitType}-subVisitActivity-${idx3}`} className="form-check form-switch d-flex justify-content-between">
                                                              <div className="w-60">
                                                                {(((subVisitActivity.abbreviation !== 'CIS' || (subVisitActivity.abbreviation === 'CIS' && checkFilter(subVisitActivity.currentStatus)))
                                                                  && (subVisitActivity.abbreviation !== 'IMA' || (subVisitActivity.abbreviation === 'IMA' && checkFilter(subVisitActivity.currentStatus))) && featureFlag) || !featureFlag)
                                                                  && (<input id={subVisitActivityInputId}
                                                                    className="form-check-input me-2" defaultChecked={subVisitActivity.toggleStatus} type="checkbox" disabled={subVisitActivity.disabled}
                                                                    onChange={(event) => handleActionChange(event, subVisitActivity)} />)}
                                                                <div className="d-flex align-items-start">
                                                                  <label id={`${componentName}-${visitType}-subVisitActivityLabel-${idx3}`} className="form-check-label d-flex justify-content-between align-items-start flex-grow-1" htmlFor={subVisitActivityInputId}>
                                                                    <div>
                                                                      {subVisitActivity?.validation === "REJECTED" && subVisitActivity?.currentStatus?.toUpperCase() === "OPEN" && (
                                                                        <i className="text-danger fa-regular fa-circle-exclamation me-1"></i>
                                                                      )}
                                                                      {parseLabel(subVisitActivity)}
                                                                    </div>
                                                                    {featureFlag && (subVisitActivity.abbreviation === 'CIS' || subVisitActivity.abbreviation === 'IMA') && (
                                                                      <div className="d-flex flex-nowrap align-items-center">
                                                                        <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={<Tooltip>Gap Ineligible
                                                                          on {formatDateModern(subVisitActivity?.immunizationsDTO?.eligibilityDate)}</Tooltip>}>
                                                                          <ProgressBar id={`${componentName}-visitProgressBar-${idx1}`} className="mvprogress me-2" now={subVisitActivity?.immunizationsDTO?.completionPercent} />
                                                                        </OverlayTrigger>
                                                                        <span id={`${componentName}-visitProgressText-${idx1}`} className="fw-bold nowrap">{subVisitActivity?.immunizationsDTO?.completionPercent}% Complete</span>
                                                                      </div>
                                                                    )}
                                                                  </label>
                                                                  {subVisitActivity?.openSource === "UI" && (subVisitActivity.createdBy === auth.getUserId()) && auth.hasAuthority("AUTH_RISK_ADJUSMENTS_FACTORS_WRITE_ACCESS") && auth.hasAuthority("AUTH_RISK_ADJUSMENTS_FACTORS_READ_ACCESS") &&
                                                                    <Button
                                                                      id={`${componentName}-deleteSuspectCondition-${idx3}`}
                                                                      type="button"
                                                                      variant="link"
                                                                      aria-label="Delete Suspect"
                                                                      title="Delete Suspect"
                                                                      onClick={(event) => {
                                                                        event.preventDefault();
                                                                        event.stopPropagation();
                                                                        handleConfirmDeleteShow(subVisitActivity.id);
                                                                      }}
                                                                      className="text-dark p-0 ms-2"
                                                                    >
                                                                      <i className="fa-solid fa-trash"></i>
                                                                    </Button>
                                                                  }
                                                                </div>
                                                                {featureFlag && !showInactiveActivity && (subVisitActivity.abbreviation === 'CIS' || subVisitActivity.abbreviation === 'IMA') && (
                                                                  <div id={`${componentName}-visitActions-${idx2}`} className="mb-3">


                                                                    {getImmuList(subVisitActivity.immunizationsDTO.careGapId).map((immuObject, immuIndex) => (
                                                                      (<ImmunizationComponent
                                                                        key={immuObject?.id ?? `${subVisitActivity.id ?? idx3}-immu-${immuIndex}`}
                                                                        immuObject={immuObject}
                                                                        id={immuObject?.id}
                                                                        showInactiveActivity={showInactiveActivity}
                                                                        handleSave={(...arg) => handleImmunizationsSave(...arg, subVisitActivity)}
                                                                      />)
                                                                    ))}
                                                                  </div>
                                                                )}
                                                                {featureFlag && showInactiveActivity && (subVisitActivity.abbreviation === 'CIS' || subVisitActivity.abbreviation === 'IMA') && (
                                                                  <div id={`${componentName}-visitActions-${idx2}`} className="mb-3">
                                                                    {checkDoses(subVisitActivity).map((immuObject, immuIndex) => (
                                                                      (<ImmunizationComponentDisabled
                                                                        key={immuObject?.id ?? `${subVisitActivity.id ?? idx3}-immu-disabled-${immuIndex}`}
                                                                        immuObject={immuObject}
                                                                        id={immuObject?.id}
                                                                      />)
                                                                    ))}
                                                                  </div>
                                                                )}
                                                              </div>
                                                            </div>)
                                                        }
                                                      </Col>
                                                    </Row>
                                                </div>
                                              )
                                            }
                                            )
                                        }
                                      </div>
                                    </div>
                                  </div>
                                )
                              }
                              )
                            : null}
                        </Col>
                      </Row>
                    )
                  }
                  ) : (null)
              }
              <Modal
                id={`${componentName}-deleteSuspectConditionModal`}
                backdropClassName="confirmbackdrop"
                show={showConfirmDelete}
                onHide={handleConfirmDeleteClose}
                size="md"
                aria-labelledby={`${componentName}-deleteSuspectConditionTitle`}
                backdrop={isDeletingSuspect ? "static" : true}
                keyboard={!isDeletingSuspect}
                enforceFocus={false}
                restoreFocus={false}
                contentClassName="pendo-ignore"
                centered
              >
                <Modal.Header>
                  <Modal.Title id={`${componentName}-deleteSuspectConditionTitle`}>Confirm Deletion</Modal.Title>
                  <button id={`${componentName}-deleteSuspectConditionClose`} type="button" className="btn-close" disabled={isDeletingSuspect} onClick={handleConfirmDeleteClose} aria-label="Close"></button>
                </Modal.Header>
                <Modal.Body>Are you sure you would like to delete this Suspect?</Modal.Body>
                <Modal.Footer>
                  <Button id={`${componentName}-deleteSuspectConditionCancel`} variant="secondary" disabled={isDeletingSuspect} onClick={handleConfirmDeleteClose}>
                    Cancel
                  </Button>
                  <Button id={`${componentName}-deleteSuspectConditionConfirm`} variant="primary" disabled={!suspectId || isDeletingSuspect} onClick={() => handleDeleteSuspect(suspectId, patient)}>
                    {isDeletingSuspect ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Deleting...
                      </>
                    ) : "Delete"}
                  </Button>
                </Modal.Footer>
              </Modal>
              { showLabValue() ? (
                <Row className="bg-light border pt-3 w-100 mx-auto mb-3">
                  <Col sm={12}>
                    <h5 id={`${componentName}-AddDetails`} className="fw-bold">
                      Additional Details
                    </h5>
                  </Col>
                  <Col sm={12}>
                    <Row>
                      <Col sm={4}>
                        <InputGroup className="border-0">
                            <Form.Label id={`${componentName}-AddDetailsA1CValueText`} className="d-block">
                              A1C Value {(requiredValues?.hasUCDM && careUCDMFeatureFlag) && <span className="text-danger">*</span>}
                            </Form.Label>
                          <Row className="g-0">
                            <Col sm={5}>
                              <Form.Control
                                id={`${componentName}-AddDetailsA1CValue1Value`}
                                type="number"
                                name="a1cWholes"
                                className="border"
                                value={preventiveCareUpdateResultsDto.a1cWholes}
                                onChange={(e) =>
                                  handlePreventiveCareUpdateResultsDtoChange(
                                    "a1cWholes",
                                    e.target.value
                                  )
                                }
                                disabled={!requiredValues.requireA1C}
                              />
                            </Col>
                            <Col sm={1} className="pt-3 text-center fw-bold">
                              .
                            </Col>
                            <Col sm={5}>
                              <Form.Control
                                id={`${componentName}-AddDetailsA1CValue2Value`}
                                type="number"
                                name="a1cValue2"
                                className="border"
                                value={preventiveCareUpdateResultsDto.a1cDecimal}
                                onChange={(e) =>
                                  handlePreventiveCareUpdateResultsDtoChange(
                                    "a1cDecimal",
                                    e.target.value
                                  )
                                }
                                disabled={!requiredValues.requireA1C}
                              />
                            </Col>
                            {ac1ValueError ? (
                              <Toast>
                                <Toast.Header>
                                  <strong className="me-auto fw-bold text-danger">
                                    <i id={`${componentName}-AddDetailsA1CValueErrorIcon`} className="fas fa-exclamation-triangle"></i>{" "}
                                  </strong>
                                  <small id={`${componentName}-AddDetailsA1CValueErrorText`}>
                                    A1C Values must be integers and cannot exceed 20.9
                                  </small>
                                </Toast.Header>
                              </Toast>
                            ) : null}
                          </Row>
                        </InputGroup>
                      </Col>
                      <Col sm={4}>
                        <InputGroup className="border-0">
                          <Form.Label id={`${componentName}-AddDetailsBloodPressValueText`} className="d-block">
                            Blood Pressure Value{requiredValues.requireBloodPressure && <span className="text-danger ms-1 fs-5 lh-1">*</span>}
                          </Form.Label>
                          <Row className="g-0">
                            <Col sm={5}>
                              <Form.Control
                                id={`${componentName}-AddDetailsBloodPress1Value`}
                                type="number"
                                name="bloodPressureSystolic"
                                className="border"
                                value={
                                  preventiveCareUpdateResultsDto.bloodPressureSystolic
                                }
                                onChange={(e) =>
                                  handlePreventiveCareUpdateResultsDtoChange(
                                    "bloodPressureSystolic",
                                    e.target.value
                                  )
                                }
                                disabled={!requiredValues.displayBloodPressure}
                              />
                            </Col>
                            <Col sm={1} className="h1 mb-0 text-center fw-bold">
                              /
                            </Col>
                            <Col sm={5}>
                              <Form.Control
                                id={`${componentName}-AddDetailsBloodPress2Value`}
                                type="number"
                                name="bloodPreasure2"
                                className="border"
                                value={
                                  preventiveCareUpdateResultsDto.bloodPressureDiastolic
                                }
                                onChange={(e) =>
                                  handlePreventiveCareUpdateResultsDtoChange(
                                    "bloodPressureDiastolic",
                                    e.target.value
                                  )
                                }
                                disabled={!requiredValues.displayBloodPressure}
                              />
                            </Col>
                          </Row>
                          <Row className="mb-3">
                            <Col>
                              <small>Systolic / Diastolic</small>
                              {bloodPressureError ? (
                                <Toast>
                                  <Toast.Header>
                                    <strong className="me-auto fw-bold text-danger">
                                      <i id={`${componentName}-AddDetailsBloodPressErrorIcon`}></i>{" "}
                                    </strong>
                                    <small id={`${componentName}-AddDetailsBloodPressErrorText`}>
                                      Must be an integer between 0 and 400
                                    </small>
                                  </Toast.Header>
                                </Toast>
                              ) : null}
                            </Col>
                          </Row>
                        </InputGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col sm={12}>
                        <Form.Group>
                          {requiredValues?.attestationDDLOptions?.length && requiredValues.requiredAttestation
                            ? requiredValues.attestationDDLOptions.map((detail, index) => (
                              <React.Fragment key={detail.gapId ?? `${componentName}-attestation-${index}`}>
                                <div
                                  id={`${componentName}-AddDetailsProvAttestationText`}
                                  className="d-block"
                                >
                                  {detail.caption}
                                  {
                                    detail.abbreviation === "EED" ? null : (<span className="text-danger ms-1 fs-5 lh-1">*</span>)
                                  }

                                </div>
                                {detail.caption.includes("OMW") ?
                                  <Filter
                                    selected={filterValues}
                                    handleChange={(e) => handleAttestationChangeForFilter(e, detail.gapId, detail.abbreviation)}
                                    filterDefinition={{
                                      type: GENERIC_DDL_TYPE,
                                      getHeader: () => "",
                                      key: "OMW",
                                      componentProps: {
                                        mode: DDLMode.MultipleSelect,
                                        dataMode: DDLDataMode.OneAPICallFilterInUI,
                                        showSearchTextbox: false,
                                        highlightWhenHasValue: false,
                                        showClearButton: true,
                                        getData: (srchTxt) => Promise.resolve([
                                          {
                                            text: "All",
                                            subItems: detail.options.map(
                                              (m) => {
                                                return { text: m.description, value: m.id };
                                              }
                                            ),
                                          },
                                        ]),
                                      },
                                    }}
                                  /> :
                                  <SimpleDDL
                                    id={`${componentName}-AddDetailsProvAttestationValue`}
                                    list={detail.options}
                                    textSelector={(x) => x.description}
                                    valueSelector={(x) => x}
                                    getKeyFromListItem={(item) =>
                                      item?.id
                                    }
                                    onChange={(x) =>
                                      handleAttestationChange(x, detail.gapId)
                                    }
                                    disabled={!requiredValues.requiredAttestation}
                                    selectedKey={
                                      preventiveCareUpdateResultsDto
                                        .careGapAdditionalInfos[detail.gapId]?.[0]?.id
                                    }
                                  />}
                              </React.Fragment>
                            ))
                            : null}
                        </Form.Group>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              ) : null}
              <Row className="mb-5">
                <Col>
                  <span id={`${componentName}-evidenceLabel`} className="fs-5 fw-bold">Evidence</span>{ isDisplayEvidenceRequiredStar() && <span id={`${componentName}-evidenceRequired`} className="text-danger">*</span>}
                  {!isEHRDocumentUploadRequired && isConnected && <p id={`${componentName}-evidenceMessage`}>Thank you for allowing Equality Health to integrate with your EHR! As a result of this partnership with us, you do not need to upload any visit documentation.</p>}
                  <Row className="mb-5">
                    <Col>
                      <FileUpload
                        id={`${componentName}-fileUpload`}
                        onChange={handleUploadFileChange}
                        onUploadingChange={setIsUploadingFile}
                        clearStateProp={fileUploadResetCounter}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className="mb-5">
                <Col>
                  <span className="fs-5 fw-bold">Add Notes</span>
                  <textarea id={`${componentName}-addNotesValue`}
                    className="form-control"
                    value={preventiveCareUpdateResultsDto.note}
                    onChange={e => {
                      setPreventiveCareUpdateResultsDto({ ...preventiveCareUpdateResultsDto, note: e.target.value });
                    }}
                    rows="3">
                  </textarea>
                </Col>
              </Row>
              {!disableUpdateButton() && checkVisitDate() && !isButtonDisabled ? (
                <Row>
                  <Col className="text-end mb-1">
                    <small className="text-black-50 fw-bold">You Have Unsaved Changes.</small>
                  </Col>
                </Row>
              ) : null}
              <Row>
                <Col className="text-end">
                  <Button id={`${componentName}-cancelBtn`} variant="secondary" data-bs-dismiss="offcanvas" className="me-3" onClick={() => setPatientDetailsShow(false)} >Cancel</Button>
                  <Button
                    id={`${componentName}-updateBtn`}
                    ref={btnRef}
                    variant="primary"
                    disabled={(!(!disableUpdateButton() && checkVisitDate() && !isButtonDisabled && !isUploadingFile) || checkLabResults())}
                    onClick={validateAndUpdate}
                  >Update</Button>
                  {isUpdating ? (
                    <Spinner
                      className="me-3"
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />

                  ) : ("")}
                </Col>
              </Row>
            </div>}
          </Col>
        </Row>
      </>}

    </>
  );
};

const EditComponent = ({ show, handleClose, setCurrentSelected, currentSelected, handleOptionChange }) => {
  const [errorFromDateInEditC, setErrorFromDateInEditC] = useState('')
  const validateCurrentDate = (e) => {
    if (e?.error) {
      setErrorFromDateInEditC(e.error)
      setCurrentSelected({ ...currentSelected, dateOfService: '' });
    }
    else {
      setCurrentSelected({ ...currentSelected, dateOfService: e.strDate });
      setErrorFromDateInEditC('')
    }
  }

  return (
    <Row id={`${componentName}-editStatusModal`} className="bg-light border me-0" size="sm" display={show} centered>
      <Col>
        <Row>
          <Col>
            <Button id={`${componentName}-bulkEditCancelBtn`} variant="link" className="float-end pb-0 px-0" onClick={handleClose} >
              <i className="fa-solid fa-close fa-lg text-dark"></i>
            </Button>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <Form.Group>
              <label id={`${componentName}-bulkEditDOSLabel`} htmlFor={`${componentName}-bulkEditDOSValue`}>
                Date of Service<span className="text-danger">*</span>
              </label>
              <EQHDatePicker
                id={`${componentName}-bulkEditDOSValue`}
                onChange={(e) => {
                  validateCurrentDate(e);
                }}
                min={null}
                max="9999-12-31"
                value={currentSelected.dateOfService || ""}
              />
              {errorFromDateInEditC ?
                <Alert key="danger" variant="danger">
                  {errorFromDateInEditC}
                </Alert> : ""}

            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <label id={`${componentName}-bulkEditStatusLabel`} htmlFor={`${componentName}-bulkEditStatusValue`}>Status<span className="text-danger">*</span></label>
            <select id={`${componentName}-bulkEditStatusValue`} className="form-select" aria-label="Status" onChange={(e) => { handleOptionChange(e) }} value={currentSelected.currentStatus?.key || ""}>
              <option value="" disabled>-Select-</option>
              {currentSelected.selected !== null && Object.keys(currentSelected.selected).map((statusKey, i) => {
                return <option
                  key={'conditionStatuses-' + i}
                  id={'conditionStatuses-' + statusKey}
                  value={statusKey}
                >
                  {statusKey}
                </option>
              })}
            </select>
          </Col>
        </Row>
      </Col>
    </Row>
  )
}

ManageVisit.propTypes = {
  patient: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    isPracticeLevelEHR: PropTypes.bool
  }),
  setPatientDetailsShow: PropTypes.func.isRequired,
  setShowFullHistory: PropTypes.func.isRequired,
  setSelectedGaps: PropTypes.func.isRequired,
  featureFlag: PropTypes.bool
};

EditComponent.propTypes = {
  show: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
  setCurrentSelected: PropTypes.func.isRequired,
  currentSelected: PropTypes.shape({
    currentStatus: PropTypes.shape({
      key: PropTypes.string
    }),
    dateOfService: PropTypes.string,
    selected: PropTypes.object
  }).isRequired,
  handleOptionChange: PropTypes.func.isRequired
};

const ImmunizationComponentDisabled = ({ immuObject, index }) => {
  return (
    <div id={`${componentName}-subVisitActivity-${index}`} className="d-flex justify-content-between" >
      <div>
        <input className="form-check-input me-2" type="checkbox" disabled={true} />
        <span className="text-black-50">{immuObject?.description ? immuObject?.description : ''} {immuObject?.immunizationAbbreviation ? `(${immuObject?.immunizationAbbreviation})` : ''}</span>
      </div>
      <Badge className={"badge bg-light rounded-pill text-dark border-0 text-dark text-opacity-50"}>{immuObject?.dosesActual}/{immuObject?.doses} Doses</Badge>
    </div>
  )

}

ImmunizationComponentDisabled.propTypes = {
  immuObject: PropTypes.shape({
    description: PropTypes.string,
    doses: PropTypes.number,
    dosesActual: PropTypes.number,
    immunizationAbbreviation: PropTypes.string
  }).isRequired,
  index: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default ManageVisit;
